import React, {useState, useEffect, useRef, useCallback} from 'react'
import {useProposalAuth} from './contexts/proposal-auth-context'
import {useProposalLink} from './hooks/use-proposal-link'
import {toast} from 'react-toastify'
import OtpInput from 'react-otp-input'
import styled from '@emotion/styled'

const OTP_EXPIRY_SECONDS = 600 // 10 minutes

const VerificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f5f6f8;
  padding: 20px;
`

const VerificationCard = styled.div`
  background: white;
  border: 1px solid #eceef1;
  border-radius: 12px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 48px 40px;
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
`

const Title = styled.h2`
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #000;
  margin: 0;
`

const Subtitle = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #4d4d4d;
  margin: 0;
  line-height: 22px;
`

const MaskedContactText = styled.span`
  font-weight: 600;
  color: #ff5000;
`

const TimerText = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  color: #4d4d4d;
  margin: 0;
`

const TimerValue = styled.span`
  font-size: 16px;
  color: #ff5000;
  font-weight: 600;
`

const ResendLink = styled.button`
  background: none;
  border: none;
  color: #ff5000;
  text-decoration: underline;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  padding: 0;

  &:disabled {
    color: #999;
    cursor: not-allowed;
    text-decoration: none;
  }
`

const SubmitButton = styled.button`
  border-radius: 8px;
  background-color: #ff5000;
  color: white;
  padding: 12px 32px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 16px;
  border: none;
  cursor: pointer;
  min-width: 160px;

  &:disabled {
    background-color: #b0b7c3;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #e04700;
  }
`

const ErrorText = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  color: #dc3545;
  margin: 0;
`

const SendButton = styled(SubmitButton)`
  margin-top: 8px;
`

function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export function ProposalOtpVerification() {
  const {randomId} = useProposalLink()
  const {
    maskedContact,
    channel,
    proposalExists,
    isLoading,
    error,
    otpSent,
    sendOtp,
    verifyOtp,
    setError,
  } = useProposalAuth()

  const channelLabel = channel === 'sms' ? 'text messages' : 'email'

  const [otp, setOtp] = useState('')
  const [countdown, setCountdown] = useState(OTP_EXPIRY_SECONDS)
  const [timerKey, setTimerKey] = useState(0)
  const timerRef = useRef(null)

  useEffect(() => {
    if (otpSent) {
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      return () => clearInterval(timerRef.current)
    }
  }, [otpSent, timerKey])

  const handleSendOtp = useCallback(async () => {
    try {
      setError(null)
      const result = await sendOtp(randomId)
      setCountdown(OTP_EXPIRY_SECONDS)
      setOtp('')
      const label = result.channel === 'sms' ? 'text messages' : 'email'
      toast.success(`Verification code sent. Please check your ${label}.`)
    } catch (err) {
      toast.error(err.message || 'Failed to send verification code')
    }
  }, [randomId, sendOtp, setError])

  const handleResendOtp = useCallback(async () => {
    try {
      setError(null)
      const result = await sendOtp(randomId)
      setCountdown(OTP_EXPIRY_SECONDS)
      setTimerKey(prev => prev + 1)
      setOtp('')
      const label = result.channel === 'sms' ? 'text messages' : 'email'
      toast.success(
        `A new verification code has been sent. Please check your ${label}.`,
      )
    } catch (err) {
      toast.error(err.message || 'Failed to resend verification code')
    }
  }, [randomId, sendOtp, setError])

  const handleVerify = useCallback(async () => {
    if (otp.length !== 6) return
    try {
      setError(null)
      await verifyOtp(randomId, otp)
      toast.success('Identity verified successfully.')
    } catch (err) {
      toast.error(err.message || 'Verification failed')
    }
  }, [randomId, otp, verifyOtp, setError])

  if (proposalExists === false) {
    return (
      <VerificationContainer>
        <VerificationCard>
          <Title>Proposal Not Found</Title>
          <Subtitle>
            This proposal link is invalid or has expired. Please contact your
            Business Consultant for a new link.
          </Subtitle>
        </VerificationCard>
      </VerificationContainer>
    )
  }

  if (proposalExists === null) {
    return (
      <VerificationContainer>
        <VerificationCard>
          <Subtitle>Loading...</Subtitle>
        </VerificationCard>
      </VerificationContainer>
    )
  }

  return (
    <VerificationContainer>
      <VerificationCard>
        <Title>Verify Your Identity</Title>

        {!otpSent ? (
          <>
            <Subtitle>
              To protect your account, we need to verify your identity. We will
              send a one-time verification code to{' '}
              <MaskedContactText>{maskedContact}</MaskedContactText>.
            </Subtitle>
            <SendButton onClick={handleSendOtp} disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Verification Code'}
            </SendButton>
          </>
        ) : (
          <>
            <Subtitle>
              We&apos;ve sent a 6-digit verification code to{' '}
              <MaskedContactText>{maskedContact}</MaskedContactText>. Please
              check your {channelLabel} and enter it below.
            </Subtitle>

            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              renderSeparator={<span style={{width: '8px'}} />}
              renderInput={inputProps => (
                <input
                  {...inputProps}
                  style={{
                    width: '48px',
                    height: '48px',
                    fontSize: '20px',
                    textAlign: 'center',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    outline: 'none',
                  }}
                />
              )}
              containerStyle={{
                display: 'flex',
                justifyContent: 'center',
              }}
            />

            {countdown > 0 ? (
              <TimerText>
                Code valid for <TimerValue>{formatTime(countdown)}</TimerValue>
              </TimerText>
            ) : (
              <TimerText style={{color: '#dc3545'}}>
                Verification code has expired.
              </TimerText>
            )}

            {error && <ErrorText>{error}</ErrorText>}

            <SubmitButton
              onClick={handleVerify}
              disabled={otp.length !== 6 || isLoading || countdown === 0}
            >
              {isLoading ? 'Verifying...' : 'Continue'}
            </SubmitButton>

            <ResendLink
              onClick={handleResendOtp}
              disabled={isLoading}
              type="button"
            >
              Didn&apos;t receive it? Resend code
            </ResendLink>
          </>
        )}
      </VerificationCard>
    </VerificationContainer>
  )
}
